import React, { useState, useEffect } from 'react';
import {
  Cog6ToothIcon,
  ShieldCheckIcon,
  BellIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UserGroupIcon,
  KeyIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
} from '@heroicons/react/24/outline';
import tenantService from '../../services/tenantService';
import { useToast } from '../../contexts/ToastContext';

const TenantSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('general');
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const data = await tenantService.getTenantSettings();
      setSettings(data);
    } catch (error) {
      showError('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await tenantService.updateTenantSettings(settings);
      showSuccess('Paramètres sauvegardés avec succès');
    } catch (error) {
      showError('Erreur lors de la sauvegarde des paramètres');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (category, field, value) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  const handleBackup = async () => {
    try {
      await tenantService.backupTenantData();
      showSuccess('Sauvegarde initiée avec succès');
    } catch (error) {
      showError('Erreur lors de la sauvegarde');
    }
  };

  const handleExport = async () => {
    try {
      const blob = await tenantService.exportTenantData('csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tenant_backup.csv';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      showSuccess('Export réussi');
    } catch (error) {
      showError('Erreur lors de l\'export');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des paramètres...</p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'general', name: 'Général', icon: Cog6ToothIcon },
    { id: 'finance', name: 'Finance', icon: CurrencyDollarIcon },
    { id: 'security', name: 'Sécurité', icon: ShieldCheckIcon },
    { id: 'notifications', name: 'Notifications', icon: BellIcon },
    { id: 'users', name: 'Utilisateurs', icon: UserGroupIcon },
    { id: 'backup', name: 'Sauvegarde', icon: CloudArrowUpIcon },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Paramètres</h1>
        <p className="mt-1 text-sm text-gray-600">
          Gérez les paramètres de votre organisation
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar */}
        <div className="lg:col-span-1">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  w-full flex items-center px-3 py-2 text-sm font-medium rounded-md
                  ${activeTab === tab.id
                    ? 'bg-gray-100 text-gray-900'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <tab.icon className="h-5 w-5 mr-3" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="lg:col-span-3">
          <div className="card">
            <div className="p-6">
              {activeTab === 'general' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres généraux</h3>
                  
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Devise
                      </label>
                      <select
                        value={settings?.general?.currency || 'FCFA'}
                        onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="FCFA">FCFA</option>
                        <option value="EUR">EUR</option>
                        <option value="USD">USD</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Langue
                      </label>
                      <select
                        value={settings?.general?.language || 'fr'}
                        onChange={(e) => handleSettingChange('general', 'language', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Fuseau horaire
                      </label>
                      <select
                        value={settings?.general?.timezone || 'Africa/Dakar'}
                        onChange={(e) => handleSettingChange('general', 'timezone', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="Africa/Dakar">Afrique/Dakar</option>
                        <option value="Europe/Paris">Europe/Paris</option>
                        <option value="UTC">UTC</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Format de date
                      </label>
                      <select
                        value={settings?.general?.date_format || 'dd/MM/yyyy'}
                        onChange={(e) => handleSettingChange('general', 'date_format', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="dd/MM/yyyy">DD/MM/YYYY</option>
                        <option value="MM/dd/yyyy">MM/DD/YYYY</option>
                        <option value="yyyy-MM-dd">YYYY-MM-DD</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'finance' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres financiers</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux d'intérêt par défaut (%)
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.default_interest_rate || 12.5}
                        onChange={(e) => handleSettingChange('finance', 'default_interest_rate', parseFloat(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                        max="50"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Taux de pénalité (%)
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.penalty_rate || 2.0}
                        onChange={(e) => handleSettingChange('finance', 'penalty_rate', parseFloat(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                        max="10"
                        step="0.1"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Solde minimum épargne (FCFA)
                      </label>
                      <input
                        type="number"
                        value={settings?.finance?.minimum_savings_balance || 0}
                        onChange={(e) => handleSettingChange('finance', 'minimum_savings_balance', parseInt(e.target.value))}
                        className="input-field mt-1"
                        min="0"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings?.finance?.auto_calculate_interest || true}
                        onChange={(e) => handleSettingChange('finance', 'auto_calculate_interest', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Calcul automatique des intérêts
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'security' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres de sécurité</h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Politique de mot de passe
                      </label>
                      <select
                        value={settings?.security?.password_policy || 'medium'}
                        onChange={(e) => handleSettingChange('security', 'password_policy', e.target.value)}
                        className="input-field mt-1"
                      >
                        <option value="low">Faible (6 caractères)</option>
                        <option value="medium">Moyenne (8 caractères, lettres et chiffres)</option>
                        <option value="high">Élevée (10 caractères, mixte, caractères spéciaux)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Durée de session (minutes)
                      </label>
                      <input
                        type="number"
                        value={settings?.security?.session_timeout || 30}
                        onChange={(e) => handleSettingChange('security', 'session_timeout', parseInt(e.target.value))}
                        className="input-field mt-1"
                        min="5"
                        max="480"
                      />
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={settings?.security?.two_factor_auth || false}
                        onChange={(e) => handleSettingChange('security', 'two_factor_auth', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                      <label className="ml-2 block text-sm text-gray-900">
                        Authentification à deux facteurs
                      </label>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'notifications' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Paramètres de notifications</h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Notifications par email
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir les notifications importantes par email
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.email_notifications || true}
                        onChange={(e) => handleSettingChange('notifications', 'email_notifications', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Notifications SMS
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir les alertes importantes par SMS
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.sms_notifications || false}
                        onChange={(e) => handleSettingChange('notifications', 'sms_notifications', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Rapports quotidiens
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir un rapport quotidien d'activité
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.daily_reports || true}
                        onChange={(e) => handleSettingChange('notifications', 'daily_reports', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>

                    <div className="flex items-center justify-between">
                      <div>
                        <label className="block text-sm font-medium text-gray-900">
                          Alertes de retard
                        </label>
                        <p className="text-sm text-gray-500">
                          Recevoir des alertes pour les prêts en retard
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        checked={settings?.notifications?.overdue_alerts || true}
                        onChange={(e) => handleSettingChange('notifications', 'overdue_alerts', e.target.checked)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                      />
                    </div>
                  </div>
                </div>
              )}

              {activeTab === 'backup' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-medium text-gray-900">Sauvegarde et export</h3>
                  
                  <div className="space-y-6">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <CloudArrowUpIcon className="h-5 w-5 text-blue-400" />
                        </div>
                        <div className="ml-3">
                          <h3 className="text-sm font-medium text-blue-800">
                            Sauvegarde automatique
                          </h3>
                          <p className="text-sm text-blue-700 mt-1">
                            Les données sont sauvegardées automatiquement tous les jours à 2h00.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <button
                        onClick={handleBackup}
                        className="flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        <ArrowPathIcon className="h-5 w-5 mr-2 text-gray-400" />
                        Sauvegarde manuelle
                      </button>

                      <button
                        onClick={handleExport}
                        className="flex items-center justify-center px-4 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700"
                      >
                        <DocumentTextIcon className="h-5 w-5 mr-2" />
                        Exporter les données
                      </button>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-sm font-medium text-gray-900 mb-4">
                        Dernières sauvegardes
                      </h4>
                      <div className="space-y-3">
                        {[
                          { date: '2024-01-15 02:00', size: '45 MB', type: 'Automatique' },
                          { date: '2024-01-14 02:00', size: '44 MB', type: 'Automatique' },
                          { date: '2024-01-13 14:30', size: '43 MB', type: 'Manuelle' },
                        ].map((backup, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(backup.date).toLocaleString('fr-FR')}
                              </p>
                              <p className="text-xs text-gray-500">{backup.type}</p>
                            </div>
                            <div className="text-sm text-gray-500">
                              {backup.size}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Save Button */}
              {activeTab !== 'backup' && (
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <div className="flex justify-end">
                    <button
                      onClick={handleSaveSettings}
                      disabled={saving}
                      className="btn-primary"
                    >
                      {saving ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sauvegarde...
                        </>
                      ) : (
                        'Sauvegarder les paramètres'
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantSettings;